import React, { useEffect, useState } from 'react';

import { Badge, Box, Code, Flex, Heading, Stack, Text } from '@chakra-ui/react';

import { DOCUMENTATION_FONT_FAMILY } from '../utils';

import { ApiCodeSamples } from './ApiCodeSamples';
import { ApiMethodBadge } from './ApiMethodBadge';
import { ApiParametersTable } from './ApiParametersTable';
import { ApiSchemaFields } from './ApiSchemaFields';
import { ApiSectionHeading } from './ApiSectionHeading';
import { ApiTryIt } from './ApiTryIt';
import { ApiOperation, ApiParameterValues } from './types';
import { initialParameterValues } from './utils';

/** One endpoint: what it does, its inputs and outputs, samples and Try it. */
export const ApiEndpointPage = ({
  operation,
  serviceTitle,
}: {
  operation: ApiOperation;
  serviceTitle: string;
}) => {
  const environmentId = new URLSearchParams(window.location.search).get(
    'environmentId',
  );
  const [values, setValues] = useState<ApiParameterValues>(() =>
    initialParameterValues(operation, environmentId),
  );
  // Another endpoint opened in the same view starts from its own defaults.
  useEffect(() => {
    setValues(initialParameterValues(operation, environmentId));
  }, [operation, environmentId]);

  const success = operation.responses.find(response =>
    response.status.startsWith('2'),
  );
  const errors = operation.responses.filter(
    response => !response.status.startsWith('2'),
  );

  return (
    <Stack spacing={10}>
      <Box as="header">
        <Text fontSize="14px" color="primary" mb={2}>
          API Reference / {serviceTitle}
        </Text>
        <Heading
          as="h1"
          fontFamily={DOCUMENTATION_FONT_FAMILY}
          fontSize="34px"
          fontWeight={600}
          color="black"
        >
          {operation.summary}
        </Heading>
        <Flex
          mt={4}
          gap={3}
          align="center"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          px={3}
          py={2}
        >
          <ApiMethodBadge method={operation.method} />
          <Code bg="transparent" fontSize="14px" wordBreak="break-all">
            {operation.path}
          </Code>
          {operation.deprecated && (
            <Badge colorScheme="yellow">Deprecated</Badge>
          )}
        </Flex>
        {operation.description && (
          <Text mt={4} fontSize="16px" lineHeight={1.7} whiteSpace="pre-line">
            {operation.description}
          </Text>
        )}
      </Box>

      {operation.parameters.length > 0 && (
        <Box>
          <ApiSectionHeading>Parameters</ApiSectionHeading>
          <ApiParametersTable parameters={operation.parameters} />
        </Box>
      )}

      {operation.requestBody?.schema && (
        <Box>
          <ApiSectionHeading>
            Request body
            {operation.requestBody.required ? '' : ' (optional)'}
          </ApiSectionHeading>
          <ApiSchemaFields schema={operation.requestBody.schema} />
        </Box>
      )}

      <Box>
        <ApiSectionHeading>Response</ApiSectionHeading>
        {success?.schema ? (
          <ApiSchemaFields schema={success.schema} />
        ) : (
          <Text fontSize="15px" color="gray.600">
            {success?.description ?? 'No response body is documented.'}
          </Text>
        )}
        {errors.length > 0 && (
          <Text fontSize="14px" color="gray.500" mt={3}>
            Other responses:{' '}
            {errors
              .map(response =>
                [response.status, response.description]
                  .filter(Boolean)
                  .join(' '),
              )
              .join(' · ')}
          </Text>
        )}
      </Box>

      <Box>
        <ApiSectionHeading>Code samples</ApiSectionHeading>
        <ApiCodeSamples operation={operation} values={values} />
      </Box>

      <Box>
        <ApiSectionHeading>Try it</ApiSectionHeading>
        <ApiTryIt operation={operation} values={values} onChange={setValues} />
      </Box>
    </Stack>
  );
};
